import {CalendarIcon} from "@hosttools/frontend";
import {
    FormControlErrorText,
    Input,
    InputField,
    InputRightAddon,
    VStack
} from "@hosttools/frontend/components";
import type {Listing} from "@hosttools/frontend/models/listing";
import isUrl from "is-url";
import type {ReactNode} from "react";
import type React from "react";
import {useCallback, useState} from "react";

import ImportCalendarItem from "./ImportCalendarItem";

type ListingCalendar = PartiallyOptional<Listing["calendars"][0], "_id">;

export type Props = {
    calendars?: ListingCalendar[];
    onChange: (newCalendars: ListingCalendar[]) => void;
};

type Error = {
    iCalURL?: string;
};

const ImportCalendars: React.FC<Props> = ({calendars = [], onChange}) => {
    const [iCalURL, setICalURL] = useState<string>("");
    const [errors, setErrors] = useState<Error>();
    const deleteCalendar = useCallback(
        (iCalURL?: string) => {
            const newCalendars = calendars.filter(calendar => {
                return calendar.iCalURL !== iCalURL;
            });
            onChange(newCalendars);
        },
        [calendars, onChange]
    );

    const addCalendar = useCallback(() => {
        const url = iCalURL.trim();
        if (!isUrl(url)) {
            setErrors({iCalURL: "Please enter a valid iCal link."});
            return;
        }
        const newCalendars: ListingCalendar[] = [...(calendars || [])];
        if (
            newCalendars.some(calendar => {
                return calendar.iCalURL === url;
            })
        ) {
            setErrors({iCalURL: "The iCal link already exists."});
            return;
        }
        newCalendars.push({iCalURL: url});
        onChange(newCalendars);
        setICalURL("");
        setErrors({});
    }, [calendars, iCalURL, onChange]);

    const icalNodes = (calendars || []).reduce<Array<ReactNode>>((result, calendar, idx) => {
        const {iCalURL, autoAdded} = calendar;

        if (iCalURL && !autoAdded) {
            result.push(
                <ImportCalendarItem key={idx} deleteCalendar={deleteCalendar} iCalURL={iCalURL} />
            );
        }

        return result;
    }, []);

    return (
        <>
            <Input leftElement={<CalendarIcon color="blueGray.600" size="md" />}>
                <InputField
                    placeholder="iCal URL..."
                    testID="input-ical-url"
                    value={iCalURL}
                    onChangeText={setICalURL}
                />
                <InputRightAddon testID="btn-add-calendar" onPress={addCalendar}>
                    + Add
                </InputRightAddon>
            </Input>
            {errors?.iCalURL && <FormControlErrorText>{errors?.iCalURL}</FormControlErrorText>}
            {!!icalNodes.length && (
                <VStack className="mt-2" space="sm">
                    {icalNodes}
                </VStack>
            )}
        </>
    );
};

export default ImportCalendars;
